import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const blue = "#0A3D91";
const blueLight = "#0D6EFD";
const borderSoft = "rgba(10,61,145,0.12)";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    backgroundColor: "#fff",
    "& fieldset": {
      borderColor: borderSoft,
    },
    "&:hover fieldset": {
      borderColor: blue,
    },
    "&.Mui-focused fieldset": {
      borderColor: blue,
    },
  },
};

const primaryButtonSx = {
  backgroundColor: blue,
  color: "#fff",
  px: 3.5,
  py: 1.25,
  borderRadius: "14px",
  fontWeight: 800,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: blueLight,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(10,61,145,0.35)",
    color: "#fff",
  },
};

const outlineButtonSx = {
  borderColor: blue,
  color: blue,
  px: 3.5,
  py: 1.25,
  borderRadius: "14px",
  fontWeight: 800,
  boxShadow: "none",
  "&:hover": {
    borderColor: blueLight,
    color: blueLight,
    backgroundColor: "rgba(10,61,145,0.04)",
    boxShadow: "none",
  },
};

const NewAccount = ({ resultado, identificacion, onBack, getCookie }) => {
  const personaInicial = resultado?.persona || null;
  const action = resultado?.action;

  const [step, setStep] = useState(
    action === "CREATE_PERSON_AND_ACCOUNT" ? "NEW_FORM" : "REQUEST_CODE"
  );
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(resultado?.message || "");
  const [error, setError] = useState("");
  const [activationToken, setActivationToken] = useState("");

  const initialIdentificacion = useMemo(() => {
    return (
      personaInicial?.identificador ||
      resultado?.identificacion ||
      identificacion ||
      ""
    );
  }, [personaInicial, resultado, identificacion]);

  const [form, setForm] = useState({
    persona_id: personaInicial?.id || "",
    identificacion: initialIdentificacion,
    tipo:
      personaInicial?.tipo ||
      (String(initialIdentificacion || "").length === 13 ? "J" : "N"),
    cedula: personaInicial?.cedula || "",
    ruc: personaInicial?.ruc || "",
    razon_social: personaInicial?.razon_social || "",
    nombre_comercial: personaInicial?.nombre_comercial || "",
    telefono: personaInicial?.telefono || "",
    correo: personaInicial?.correo || "",
    direccion: personaInicial?.direccion || "",
  });

  const [activation, setActivation] = useState({
    codigo: "",
    password: "",
    password_confirm: "",
  });

  const updateForm = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateActivation = (field, value) => {
    setActivation((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const headers = () => ({
    "Content-Type": "application/json",
    "X-CSRFToken": getCookie("csrftoken"),
  });

  const cargarPersonaVerificada = (persona) => {
    setForm((prev) => ({
      ...prev,
      persona_id: persona?.id || prev.persona_id,
      identificacion: persona?.identificador || prev.identificacion,
      tipo: persona?.tipo || prev.tipo,
      cedula: persona?.cedula || "",
      ruc: persona?.ruc || "",
      razon_social: persona?.razon_social || "",
      nombre_comercial: persona?.nombre_comercial || "",
      telefono: persona?.telefono || "",
      correo: persona?.correo || "",
      direccion: persona?.direccion || "",
    }));
  };

  const solicitarCodigoExistente = async () => {
    setError("");
    setMessage("");
    setLoading(true);

    try {
      const response = await fetch("/clientes/public/solicitar-activacion/", {
        method: "POST",
        credentials: "same-origin",
        headers: headers(),
        body: JSON.stringify({
          persona_id: form.persona_id,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        setError(data.message || "No se pudo enviar el código de activación.");
        return;
      }

      setMessage(data.message || "Código enviado correctamente.");
      setStep("EXISTING_CODE");
    } catch (err) {
      setError("No se pudo conectar con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  const solicitarCodigoNuevo = async () => {
    setError("");
    setMessage("");

    if (!form.razon_social.trim()) {
      setError("Ingrese la razón social o nombre completo.");
      return;
    }

    if (!form.correo.trim()) {
      setError("Ingrese un correo electrónico.");
      return;
    }

    if (!form.direccion.trim()) {
      setError("Ingrese la dirección.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/clientes/public/solicitar-activacion/", {
        method: "POST",
        credentials: "same-origin",
        headers: headers(),
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        setError(
          typeof data.message === "string"
            ? data.message
            : "No se pudo enviar el código de activación."
        );
        return;
      }

      setForm((prev) => ({
        ...prev,
        persona_id: data.persona?.id || prev.persona_id,
        cedula: data.persona?.cedula || prev.cedula,
        ruc: data.persona?.ruc || prev.ruc,
      }));

      setMessage(data.message || "Código enviado correctamente.");
      setStep("NEW_CODE");
    } catch (err) {
      setError("No se pudo conectar con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  const validarCodigoExistente = async () => {
    setError("");
    setMessage("");

    if (!activation.codigo || activation.codigo.length !== 8) {
      setError("Ingrese el código de 8 dígitos.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/clientes/public/validar-codigo/", {
        method: "POST",
        credentials: "same-origin",
        headers: headers(),
        body: JSON.stringify({
          persona_id: form.persona_id,
          codigo: activation.codigo,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        setError(data.message || "No se pudo validar el código.");
        return;
      }

      setActivationToken(data.activation_token || "");
      cargarPersonaVerificada(data.persona);
      setMessage(
        data.message ||
          "Código validado correctamente. Ahora puede actualizar sus datos."
      );
      setStep("EXISTING_FORM");
    } catch (err) {
      setError("No se pudo conectar con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  const validarCodigoNuevo = async () => {
    setError("");
    setMessage("");

    if (!activation.codigo || activation.codigo.length !== 8) {
      setError("Ingrese el código de 8 dígitos.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/clientes/public/validar-codigo/", {
        method: "POST",
        credentials: "same-origin",
        headers: headers(),
        body: JSON.stringify({
          persona_id: form.persona_id,
          codigo: activation.codigo,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        setError(data.message || "No se pudo validar el código.");
        return;
      }

      setActivationToken(data.activation_token || "");
      cargarPersonaVerificada(data.persona);
      setMessage(
        data.message ||
          "Código validado correctamente. Ahora puede crear su contraseña."
      );
      setStep("NEW_PASSWORD");
    } catch (err) {
      setError("No se pudo conectar con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  const activarCuenta = async () => {
    setError("");
    setMessage("");

    if (!activationToken) {
      setError("Debe validar el código antes de activar la cuenta.");
      return;
    }

    if (!activation.password || activation.password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    if (activation.password !== activation.password_confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    if (!form.razon_social.trim()) {
      setError("Ingrese la razón social o nombre completo.");
      return;
    }

    if (!form.direccion.trim()) {
      setError("Ingrese la dirección.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/clientes/public/activar-cuenta/", {
        method: "POST",
        credentials: "same-origin",
        headers: headers(),
        body: JSON.stringify({
          activation_token: activationToken,
          tipo: form.tipo,
          razon_social: form.razon_social,
          nombre_comercial: form.nombre_comercial,
          telefono: form.telefono,
          direccion: form.direccion,
          password: activation.password,
          password_confirm: activation.password_confirm,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        setError(data.message || "No se pudo activar la cuenta.");
        return;
      }

      setMessage(data.message || "Cuenta activada correctamente.");
      setStep("DONE");
    } catch (err) {
      setError("No se pudo conectar con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  if (action === "ACCOUNT_EXISTS") {
    return (
      <Box>
        <Alert severity="info" sx={{ borderRadius: "14px", mb: 3 }}>
          Esta identificación ya tiene una cuenta creada. Debe iniciar sesión o
          recuperar su contraseña. Si el correo registrado no es suyo o ya no
          tiene acceso a ese correo, comuníquese con servicio al cliente para
          solicitar el cambio de correo.
        </Alert>

        <Box
          sx={{
            border: `1px solid ${borderSoft}`,
            borderRadius: "18px",
            p: { xs: 2.5, md: 3 },
            mb: 3,
          }}
        >
          <Typography
            sx={{ fontSize: "1.05rem", fontWeight: 800, color: blue, mb: 2 }}
          >
            Registro encontrado
          </Typography>

          <Stack spacing={1.3}>
            <Dato label="Cédula" value={personaInicial?.cedula} />
            <Dato label="RUC" value={personaInicial?.ruc} />
            <Dato
              label="Correo registrado"
              value={personaInicial?.correo_masked || "No registrado"}
            />
          </Stack>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <Button href="/login" variant="contained" sx={primaryButtonSx}>
            INICIAR SESIÓN
          </Button>

          <Button onClick={onBack} variant="outlined" sx={outlineButtonSx}>
            CONSULTAR OTRA IDENTIFICACIÓN
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      {message && (
        <Alert
          severity={step === "DONE" ? "success" : "info"}
          sx={{ borderRadius: "14px", mb: 3 }}
        >
          {message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ borderRadius: "14px", mb: 3 }}>
          {error}
        </Alert>
      )}

      {step === "REQUEST_CODE" && (
        <Box>
          <Typography
            sx={{ fontSize: "1.25rem", fontWeight: 800, color: blue, mb: 1 }}
          >
            Validar correo registrado
          </Typography>

          <Typography
            sx={{
              fontSize: "0.98rem",
              lineHeight: 1.7,
              color: "rgba(0,0,0,0.68)",
              mb: 3,
            }}
          >
            Encontramos un registro asociado a esta identificación. Para crear
            la cuenta y actualizar datos, primero debe solicitar un código al
            correo registrado.
          </Typography>

          <Box
            sx={{
              border: `1px solid ${borderSoft}`,
              borderRadius: "18px",
              p: { xs: 2.5, md: 3 },
              mb: 3,
            }}
          >
            <Stack spacing={1.3}>
              <Dato label="Cédula" value={personaInicial?.cedula} />
              <Dato label="RUC" value={personaInicial?.ruc} />
              <Dato
                label="Correo registrado"
                value={personaInicial?.correo_masked || "No registrado"}
              />
            </Stack>
          </Box>

          {!personaInicial?.tiene_correo && (
            <Alert severity="warning" sx={{ borderRadius: "14px", mb: 3 }}>
              Este registro no tiene correo registrado. Comuníquese con servicio
              al cliente para actualizar su correo y poder activar la cuenta.
            </Alert>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              onClick={solicitarCodigoExistente}
              disabled={loading || !personaInicial?.tiene_correo}
              variant="contained"
              sx={primaryButtonSx}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "ENVIAR CÓDIGO"
              )}
            </Button>

            <Button onClick={onBack} variant="outlined" sx={outlineButtonSx}>
              VOLVER
            </Button>
          </Stack>

          <Typography
            sx={{
              mt: 3,
              fontSize: "0.92rem",
              lineHeight: 1.7,
              color: "rgba(0,0,0,0.62)",
            }}
          >
            Si el correo registrado no es suyo o ya no tiene acceso a ese
            correo, debe comunicarse con servicio al cliente para solicitar el
            cambio de correo.
          </Typography>
        </Box>
      )}

      {step === "NEW_FORM" && (
        <Box>
          <Typography
            sx={{ fontSize: "1.25rem", fontWeight: 800, color: blue, mb: 1 }}
          >
            Crear cuenta de cliente
          </Typography>

          <Typography
            sx={{
              fontSize: "0.98rem",
              lineHeight: 1.7,
              color: "rgba(0,0,0,0.68)",
              mb: 3,
            }}
          >
            Complete sus datos. Luego enviaremos un código al correo ingresado
            para activar la cuenta.
          </Typography>

          <DatosForm form={form} updateForm={updateForm} showCorreo />

          <Divider sx={{ my: 3 }} />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              onClick={solicitarCodigoNuevo}
              disabled={loading}
              variant="contained"
              sx={primaryButtonSx}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "ENVIAR CÓDIGO"
              )}
            </Button>

            <Button onClick={onBack} variant="outlined" sx={outlineButtonSx}>
              VOLVER
            </Button>
          </Stack>
        </Box>
      )}

      {step === "EXISTING_CODE" && (
        <Box>
          <Typography
            sx={{ fontSize: "1.25rem", fontWeight: 800, color: blue, mb: 1 }}
          >
            Ingrese el código recibido
          </Typography>

          <Typography
            sx={{
              fontSize: "0.98rem",
              lineHeight: 1.7,
              color: "rgba(0,0,0,0.68)",
              mb: 3,
            }}
          >
            Solo después de validar el código podrá ver y actualizar sus datos
            registrados.
          </Typography>

          <TextField
            label="Código de 8 dígitos"
            value={activation.codigo}
            onChange={(e) =>
              updateActivation(
                "codigo",
                e.target.value.replace(/\D/g, "").slice(0, 8)
              )
            }
            fullWidth
            sx={{ ...inputSx, maxWidth: "360px", mb: 3 }}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              onClick={validarCodigoExistente}
              disabled={loading}
              variant="contained"
              sx={primaryButtonSx}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "VALIDAR CÓDIGO"
              )}
            </Button>

            <Button
              onClick={solicitarCodigoExistente}
              disabled={loading}
              variant="outlined"
              sx={outlineButtonSx}
            >
              REENVIAR CÓDIGO
            </Button>
          </Stack>
        </Box>
      )}

      {step === "EXISTING_FORM" && (
        <Box>
          <Typography
            sx={{ fontSize: "1.25rem", fontWeight: 800, color: blue, mb: 1 }}
          >
            Actualizar datos y activar cuenta
          </Typography>

          <Typography
            sx={{
              fontSize: "0.98rem",
              lineHeight: 1.7,
              color: "rgba(0,0,0,0.68)",
              mb: 3,
            }}
          >
            Estos son los datos registrados en el sistema. Puede actualizar los
            datos permitidos antes de crear su contraseña. La cédula, el RUC y
            el correo no se pueden modificar desde este formulario.
          </Typography>

          <DatosForm form={form} updateForm={updateForm} showCorreo bloquearCorreo />

          <PasswordForm
            activation={activation}
            updateActivation={updateActivation}
            includeCode={false}
          />

          <Divider sx={{ my: 3 }} />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              onClick={activarCuenta}
              disabled={loading}
              variant="contained"
              sx={primaryButtonSx}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "ACTIVAR CUENTA"
              )}
            </Button>

            <Button onClick={onBack} variant="outlined" sx={outlineButtonSx}>
              CANCELAR
            </Button>
          </Stack>
        </Box>
      )}

      {step === "NEW_CODE" && (
        <Box>
          <Typography
            sx={{ fontSize: "1.25rem", fontWeight: 800, color: blue, mb: 1 }}
          >
            Ingrese el código recibido
          </Typography>

          <Typography
            sx={{
              fontSize: "0.98rem",
              lineHeight: 1.7,
              color: "rgba(0,0,0,0.68)",
              mb: 3,
            }}
          >
            Ingrese el código enviado al correo para continuar con la creación
            de su contraseña.
          </Typography>

          <TextField
            label="Código de 8 dígitos"
            value={activation.codigo}
            onChange={(e) =>
              updateActivation(
                "codigo",
                e.target.value.replace(/\D/g, "").slice(0, 8)
              )
            }
            fullWidth
            sx={{ ...inputSx, maxWidth: "360px", mb: 3 }}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              onClick={validarCodigoNuevo}
              disabled={loading}
              variant="contained"
              sx={primaryButtonSx}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "VALIDAR CÓDIGO"
              )}
            </Button>

            <Button
              onClick={solicitarCodigoNuevo}
              disabled={loading}
              variant="outlined"
              sx={outlineButtonSx}
            >
              REENVIAR CÓDIGO
            </Button>
          </Stack>
        </Box>
      )}

      {step === "NEW_PASSWORD" && (
        <Box>
          <Typography
            sx={{ fontSize: "1.25rem", fontWeight: 800, color: blue, mb: 1 }}
          >
            Crear contraseña
          </Typography>

          <Typography
            sx={{
              fontSize: "0.98rem",
              lineHeight: 1.7,
              color: "rgba(0,0,0,0.68)",
              mb: 3,
            }}
          >
            El código fue validado correctamente. Ahora cree su contraseña para
            activar la cuenta.
          </Typography>

          <DatosForm form={form} updateForm={updateForm} showCorreo bloquearCorreo />

          <PasswordForm
            activation={activation}
            updateActivation={updateActivation}
            includeCode={false}
          />

          <Divider sx={{ my: 3 }} />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              onClick={activarCuenta}
              disabled={loading}
              variant="contained"
              sx={primaryButtonSx}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "ACTIVAR CUENTA"
              )}
            </Button>

            <Button onClick={onBack} variant="outlined" sx={outlineButtonSx}>
              CANCELAR
            </Button>
          </Stack>
        </Box>
      )}

      {step === "DONE" && (
        <Box>
          <Typography
            sx={{ fontSize: "1.25rem", fontWeight: 800, color: blue, mb: 1 }}
          >
            Cuenta activada
          </Typography>

          <Typography
            sx={{
              fontSize: "0.98rem",
              lineHeight: 1.7,
              color: "rgba(0,0,0,0.68)",
              mb: 3,
            }}
          >
            Su cuenta fue creada correctamente. Ahora puede iniciar sesión para
            consultar sus documentos, compras y beneficios.
          </Typography>

          <Button href="/login" variant="contained" sx={primaryButtonSx}>
            IR AL LOGIN
          </Button>
        </Box>
      )}
    </Box>
  );
};

const DatosForm = ({
  form,
  updateForm,
  showCorreo = false,
  bloquearCorreo = false,
}) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 2,
      }}
    >
      <TextField
        label="Cédula"
        value={form.cedula || ""}
        disabled
        fullWidth
        sx={inputSx}
      />

      <TextField
        label="RUC"
        value={form.ruc || ""}
        disabled
        fullWidth
        sx={inputSx}
      />

      <FormControl fullWidth sx={inputSx}>
        <InputLabel>Tipo</InputLabel>
        <Select
          label="Tipo"
          value={form.tipo}
          onChange={(e) => updateForm("tipo", e.target.value)}
        >
          <MenuItem value="N">Natural</MenuItem>
          <MenuItem value="J">Jurídica</MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Razón social / Nombre completo"
        value={form.razon_social}
        onChange={(e) => updateForm("razon_social", e.target.value)}
        fullWidth
        sx={inputSx}
      />

      <TextField
        label="Nombre comercial"
        value={form.nombre_comercial}
        onChange={(e) => updateForm("nombre_comercial", e.target.value)}
        fullWidth
        sx={inputSx}
      />

      <TextField
        label="Teléfono"
        value={form.telefono}
        onChange={(e) =>
          updateForm("telefono", e.target.value.replace(/\D/g, "").slice(0, 20))
        }
        fullWidth
        sx={inputSx}
      />

      {showCorreo && (
        <TextField
          label="Correo electrónico"
          value={form.correo}
          disabled={bloquearCorreo}
          onChange={(e) => updateForm("correo", e.target.value)}
          fullWidth
          sx={inputSx}
        />
      )}

      <TextField
        label="Dirección"
        value={form.direccion}
        onChange={(e) => updateForm("direccion", e.target.value)}
        fullWidth
        multiline
        minRows={2}
        sx={{
          ...inputSx,
          gridColumn: { xs: "auto", md: showCorreo ? "1 / 3" : "1 / 3" },
        }}
      />
    </Box>
  );
};

const PasswordForm = ({ activation, updateActivation, includeCode = false }) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 2,
        mt: 3,
      }}
    >
      {includeCode && (
        <TextField
          label="Código de 8 dígitos"
          value={activation.codigo}
          onChange={(e) =>
            updateActivation(
              "codigo",
              e.target.value.replace(/\D/g, "").slice(0, 8)
            )
          }
          fullWidth
          sx={inputSx}
        />
      )}

      {includeCode && <Box sx={{ display: { xs: "none", md: "block" } }} />}

      <TextField
        label="Contraseña"
        type="password"
        value={activation.password}
        onChange={(e) => updateActivation("password", e.target.value)}
        fullWidth
        sx={inputSx}
      />

      <TextField
        label="Confirmar contraseña"
        type="password"
        value={activation.password_confirm}
        onChange={(e) =>
          updateActivation("password_confirm", e.target.value)
        }
        fullWidth
        sx={inputSx}
      />
    </Box>
  );
};

const Dato = ({ label, value }) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "180px 1fr" },
        gap: { xs: 0.3, sm: 1.5 },
      }}
    >
      <Typography
        sx={{
          fontSize: "0.9rem",
          fontWeight: 800,
          color: "rgba(0,0,0,0.60)",
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: "0.95rem",
          fontWeight: 600,
          color: "#111",
          wordBreak: "break-word",
        }}
      >
        {value || "No registrado"}
      </Typography>
    </Box>
  );
};

export default NewAccount;